import type { Language } from "@/domain/common";
import type { TagRef } from "@/domain/tag";

// Enums matching backend
export const INVESTOR_TYPES = ["individual", "company"] as const;
export type InvestorType = (typeof INVESTOR_TYPES)[number];
export const INVESTOR_STATUSES = ["active", "inactive", "archived"] as const;
export type InvestorStatus = (typeof INVESTOR_STATUSES)[number];

// Hub access status matching backend HubStatus enum
export type HubStatus = "none" | "pending" | "active";

// Cultural activity types matching backend enum
export type CulturalActivity =
  | "film"
  | "documentary"
  | "series"
  | "theatre"
  | "dance"
  | "music_festival"
  | "performing_arts"
  | "audiovisual_production";

export const CULTURAL_ACTIVITIES: readonly CulturalActivity[] = [
  "film",
  "documentary",
  "series",
  "theatre",
  "dance",
  "music_festival",
  "performing_arts",
  "audiovisual_production",
];

export interface InvestorMoney {
  potential: string | null;
  investment: {
    reserved: string;
    completed: string;
    confirmed: string;
    committed: string;
  };
  available: string | null;
  deduction: {
    committed: string;
    by_incentive: {
      performing_arts_music: string;
      film_audiovisual: string;
    };
  };
}

// Summary investor data matching backend InvestorSummary
export interface InvestorSummary {
  uuid: string;
  type: InvestorType;
  status: InvestorStatus;
  display_name?: string;
  email?: string | null;
  tax_identifier?: string | null;
  city?: string | null;
}

// Investor with investment summary matching backend InvestorWithInvestmentSummaryResponse
export interface InvestorWithInvestmentSummary {
  uuid: string;
  tags: TagRef[];
  type: InvestorType;
  status: InvestorStatus;
  first_name?: string | null;
  last_name?: string | null;
  company_name?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  country?: string | null;
  taxable_income_base?: string | null;
  potential_investment_amount?: string | null;
  cultural_interests?: CulturalActivity[];
  number_investments: number;
  money: InvestorMoney;
  // Computed fields
  display_name: string;
  tax_identifier?: string | null;
  created_at: string;
}

// Shared response fields for both investor types (contact, financial,
// computed and lifecycle data straight from the wire).
interface InvestorResponseBase {
  uuid: string;
  organization_id: number;
  status: InvestorStatus;
  tags: TagRef[];

  // Contact & address fields
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  communication_language: Language;
  notes?: string | null;

  // Financial & Interest fields
  taxable_income_base?: string | null;
  potential_investment_amount?: string | null;
  cultural_interests: CulturalActivity[];

  // Computed fields
  display_name: string;
  full_address?: string | null;
  tax_identifier: string;

  // Hub access status
  hub_status: HubStatus;
  hub_invitation_uuid: string | null;

  // Timestamps
  created_at: string;
  updated_at?: string | null;
}

// Individual investor response matching backend IndividualResponse
export interface IndividualInvestor extends InvestorResponseBase {
  type: "individual";
  first_name: string;
  last_name: string;
  national_id: string;
  birth_date?: string | null;
  profession?: string | null;
  full_name: string;
}

/**
 * How many representatives must sign for the investor party.
 * `sole`: one signature closes the investor side.
 * `joint`: every representative flagged as signer must sign.
 */
export type SignatureRegime = "sole" | "joint";

export const SIGNATURE_REGIMES: SignatureRegime[] = ["sole", "joint"];

/**
 * The backend refuses to dispatch a contract for a joint party with fewer
 * than two signers, and the PDF signature grid only has cells for two.
 */
export const JOINT_MIN_SIGNERS = 2;

// Mirrors the backend MAX_SIGNERS_PER_IDENTITY: the signature block renders
// at most two cells per party, so at most two representatives can sign.
export const JOINT_MAX_SIGNERS = 2;

// Matches backend InvestorLegalRepresentativeResponse exactly
export interface InvestorLegalRepresentative {
  uuid: string;
  first_name: string;
  last_name: string;
  full_name: string;
  dni: string;
  position: string | null;
  phone: string | null;
  email: string | null;
  is_primary: boolean;
  is_signer: boolean;
  created_at: string;
  updated_at: string;
}

// Matches backend InvestorLegalRepresentativeCreateRequest
export interface InvestorLegalRepCreateRequest {
  first_name: string;
  last_name: string;
  dni: string;
  position?: string | null;
  phone?: string | null;
  email?: string | null;
  is_primary?: boolean;
  is_signer?: boolean;
}

// Matches backend InvestorLegalRepresentativeUpdateRequest
export interface InvestorLegalRepUpdateRequest {
  first_name?: string;
  last_name?: string;
  dni?: string;
  position?: string | null;
  phone?: string | null;
  email?: string | null;
  is_primary?: boolean;
  is_signer?: boolean;
}

// Company investor response matching backend CompanyResponse
export interface CompanyInvestor extends InvestorResponseBase {
  type: "company";
  company_name: string;
  tax_id: string;
  legal_rep_first_name?: string | null;
  legal_rep_last_name?: string | null;
  legal_rep_national_id?: string | null;
  website?: string | null;
  legal_representative_name?: string | null;
  signature_regime: SignatureRegime;
  legal_representatives: InvestorLegalRepresentative[];
}

// Union type for any investor
export type Investor = IndividualInvestor | CompanyInvestor;

// Shared request fields for creating/updating investors.
interface InvestorRequestBase {
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  communication_language?: Language;
  taxable_income_base?: string | null;
  potential_investment_amount?: string | null;
  cultural_interests?: CulturalActivity[];
}

// Request types for creating investors
export interface CreateIndividualInvestorRequest extends InvestorRequestBase {
  first_name: string;
  last_name: string;
  national_id: string;
  birth_date?: string | null;
  profession?: string | null;
}

export interface CreateCompanyInvestorRequest extends InvestorRequestBase {
  company_name: string;
  tax_id: string;
  legal_rep_first_name?: string | null;
  legal_rep_last_name?: string | null;
  legal_rep_national_id?: string | null;
  website?: string;
  signature_regime?: SignatureRegime;
  legal_representatives?: InvestorLegalRepCreateRequest[];
}

// Request types for updating investors
export interface UpdateIndividualInvestorRequest extends InvestorRequestBase {
  status?: InvestorStatus;
  first_name?: string;
  last_name?: string;
  national_id?: string;
  birth_date?: string | null;
  profession?: string | null;
}

export interface UpdateCompanyInvestorRequest extends InvestorRequestBase {
  status?: InvestorStatus;
  company_name?: string;
  tax_id?: string;
  legal_rep_first_name?: string | null;
  legal_rep_last_name?: string | null;
  legal_rep_national_id?: string | null;
  website?: string;
  signature_regime?: SignatureRegime;
}

// Investor operation response matching backend
export interface InvestorOperationResponse {
  uuid: string;
  type: InvestorType;
  status: InvestorStatus;
  display_name: string;
  message?: string;
}

// Paginated list response matching backend
export interface InvestorListResponse {
  investors: InvestorWithInvestmentSummary[];
  total_count: number;
  limit: number;
  offset: number;
}

// Document types matching backend InvestorDocumentType enum
export enum InvestorDocumentType {
  // Identification documents
  NATIONAL_ID = "national_id",
  PASSPORT = "passport",
  RESIDENCE_PERMIT = "residence_permit",

  // Tax & Financial documents
  TAX_CERTIFICATE = "tax_certificate",
  INCOME_STATEMENT = "income_statement",
  WEALTH_DECLARATION = "wealth_declaration",

  // Corporate documents (for company investors)
  COMPANY_REGISTRATION = "company_registration",
  ARTICLES_OF_ASSOCIATION = "articles_of_association",
  POWER_OF_ATTORNEY = "power_of_attorney",

  // Banking documents
  BANK_ACCOUNT_CERTIFICATE = "bank_account_certificate",
  IBAN_CERTIFICATE = "iban_certificate",

  // Other documents
  KYC_FORM = "kyc_form",
  PROOF_OF_ADDRESS = "proof_of_address",
  OTHER = "other",
}

export type InvestorSortField =
  | "name"
  | "type"
  | "number_investments"
  | "money.potential"
  | "money.available"
  | "money.investment.committed"
  | "created_at";

export interface InvestorSort {
  key: InvestorSortField;
  direction: "asc" | "desc";
}

export interface InvestorListFilters {
  search?: string;
  status?: InvestorStatus[];
  investor_type?: InvestorType[];
  tag?: string[];
  uuid?: string;
  fiscal_year?: number;
}

export interface InvestorListParams extends InvestorListFilters {
  sort?: InvestorSort[];
  limit?: number;
  offset?: number;
}

export interface InvestorExportParams extends InvestorListFilters {
  sort?: InvestorSort[];
}

export const INVESTOR_AGGREGATE_FUNCTIONS = {
  number_investments: ["sum", "avg", "min", "max"],
  "money.potential": ["sum", "avg", "min", "max"],
  "money.investment.reserved": ["sum", "avg", "min", "max"],
  "money.investment.completed": ["sum", "avg", "min", "max"],
  "money.investment.confirmed": ["sum", "avg", "min", "max"],
  "money.investment.committed": ["sum", "avg", "min", "max"],
  "money.available": ["sum", "avg", "min", "max"],
  email: ["filled", "empty", "unique"],
  city: ["filled", "empty", "unique"],
  type: ["distribution"],
  tag: ["distribution"],
  status: ["active", "inactive", "archived", "pct_active"],
} as const;

type InvestorAggregateField = keyof typeof INVESTOR_AGGREGATE_FUNCTIONS;

type InvestorAggregatePair<F extends InvestorAggregateField> = F extends InvestorAggregateField
  ? `${F}:${(typeof INVESTOR_AGGREGATE_FUNCTIONS)[F][number]}`
  : never;

export type InvestorAggregateKey = InvestorAggregatePair<InvestorAggregateField>;

export type InvestorAggregates = Partial<
  Record<InvestorAggregateKey, number | string | Record<string, number> | null>
>;

export interface InvestorAggregatesParams extends InvestorListFilters {
  aggregates: InvestorAggregateKey[];
}

export interface HubInvitationRequest {
  message?: string | null;
}

// Matches backend InvestorMetricsResponse exactly
interface InvestorTypeBreakdown {
  individual: number;
  company: number;
}

export interface InvestorMetrics {
  total_count: number;
  by_type: InvestorTypeBreakdown;
  potential_capacity: string; // Decimal as string from API
  committed: string; // Decimal as string from API
  available: string; // Decimal as string from API
  reserved: string; // Decimal as string from API
  completed: string; // Decimal as string from API
}
